// A guest's own wedding invitation (no login): the link in their WhatsApp/email opens it. They
// see the details and reply — attending, not attending or maybe — which updates the family's
// guest list.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Stack, Button, TextField, Alert, CircularProgress, Divider, Link, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import MapIcon from '@mui/icons-material/Map';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import PhoneIcon from '@mui/icons-material/Phone';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LegalFooter from '../components/LegalFooter';

const API_ORIGIN = process.env.REACT_APP_GRAPHQL_URL ? new URL(process.env.REACT_APP_GRAPHQL_URL).origin : '';

const REPLIES = [
  { value: 'attending', label: 'Yes, I will attend' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'not_attending', label: "Sorry, I can't" },
];
const THANKS = {
  attending: 'Thank you! We look forward to celebrating with you.',
  maybe: 'Thank you for letting us know. Do update your reply when you can.',
  not_attending: 'Thank you for letting us know. You will be missed!',
};

function Line({ icon, children }) {
  if (!children) return null;
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ color: '#7b1fa2', pt: 0.25 }}>{icon}</Box>
      <Typography variant="body1">{children}</Typography>
    </Stack>
  );
}

export default function InviteRsvpPage() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [guestsCount, setGuestsCount] = useState(1);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [optedOut, setOptedOut] = useState(false);

  useEffect(() => {
    fetch(`${API_ORIGIN}/public/invite/${token}`)
      .then(async r => { const body = await r.json(); if (!r.ok) throw new Error(body.error); return body; })
      .then(body => {
        setInv(body);
        if (body.rsvpResponse && body.rsvpResponse !== 'pending') setReply(body.rsvpResponse);
        if (body.guestsCount) setGuestsCount(body.guestsCount);
        if (body.rsvpNote) setNote(body.rsvpNote);
        setOptedOut(Boolean(body.optedOut));
      })
      .catch(e => setError(e.message || 'This invitation could not be opened.'));
  }, [token]);

  const send = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_ORIGIN}/public/invite/${token}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: reply, guestsCount, note }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error);
      setSaved(THANKS[reply]);
    } catch (e) { setError(e.message || 'Your reply could not be sent. Please try again.'); }
    setSaving(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fdf6ff', py: { xs: 3, sm: 6 }, px: 2 }}>
      <Container maxWidth="sm" disableGutters>
        {!inv && !error && <Stack alignItems="center" mt={10}><CircularProgress /></Stack>}
        {!inv && error && <Alert severity="error">{error}</Alert>}
        {inv && (
          <Paper elevation={4} sx={{ borderRadius: 4, overflow: 'hidden', border: '2px solid #c9a227' }}>
            <Box sx={{ background: 'linear-gradient(135deg,#7b1fa2,#c2185b)', color: '#fff', textAlign: 'center', py: 4, px: 2 }}>
              <Typography variant="overline" sx={{ letterSpacing: 3, opacity: 0.9 }}>
                {inv.ceremonyName && !/wedding/i.test(inv.ceremonyName) ? inv.ceremonyName : 'Wedding Invitation'}
              </Typography>
              <Typography variant="h4" fontFamily="Georgia, serif" mt={1}>{inv.couple}</Typography>
              <FavoriteIcon sx={{ mt: 1, opacity: 0.85 }} />
            </Box>

            <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
              <Typography variant="h6" fontWeight={600}>Dear {inv.guestName},</Typography>
              <Typography variant="body1" color="text.secondary" mt={1} mb={2.5}>
                With the blessings of our elders, we joyfully invite you to celebrate the wedding of <b>{inv.couple}</b>.
                Your presence will make the occasion special.
              </Typography>

              <Stack spacing={1.5} mb={2.5}>
                <Line icon={<EventIcon />}>{[inv.eventDateText, inv.eventTime].filter(Boolean).join(', ')}</Line>
                <Line icon={<PlaceIcon />}>{[inv.venue, inv.address].filter(Boolean).join(', ')}</Line>
                {inv.mapLink && (
                  <Line icon={<MapIcon />}><Link href={inv.mapLink} target="_blank" rel="noopener noreferrer">Open the map</Link></Line>
                )}
                <Line icon={<CheckroomIcon />}>{inv.dressCode && `Dress code: ${inv.dressCode}`}</Line>
              </Stack>

              {inv.message && (
                <Typography variant="body1" sx={{ fontStyle: 'italic', borderLeft: '3px solid #c9a227', pl: 1.5, mb: 2.5, whiteSpace: 'pre-line' }}>
                  {inv.message}
                </Typography>
              )}

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="subtitle1" fontWeight={700} mb={1}>
                Will you join us?{inv.rsvpBy ? ` Kindly reply by ${inv.rsvpBy}.` : ''}
              </Typography>
              {saved ? (
                <Alert severity="success" action={<Button color="inherit" size="small" onClick={() => setSaved('')}>Change</Button>}>{saved}</Alert>
              ) : (
                <Stack spacing={2}>
                  {error && <Alert severity="error">{error}</Alert>}
                  <ToggleButtonGroup
                    exclusive color="primary" value={reply} onChange={(_, v) => v && setReply(v)}
                    orientation="vertical" fullWidth sx={{ display: { xs: 'flex', sm: 'none' } }}
                  >
                    {REPLIES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                  <ToggleButtonGroup
                    exclusive color="primary" value={reply} onChange={(_, v) => v && setReply(v)}
                    fullWidth sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    {REPLIES.map(r => <ToggleButton key={r.value} value={r.value}>{r.label}</ToggleButton>)}
                  </ToggleButtonGroup>
                  {reply && reply !== 'not_attending' && (
                    <TextField
                      type="number" label="How many of you are coming (including you)?" value={guestsCount}
                      onChange={e => setGuestsCount(e.target.value)} inputProps={{ min: 1, max: 20 }}
                    />
                  )}
                  {reply && (
                    <TextField label="A message for the family (optional)" multiline minRows={2} value={note} onChange={e => setNote(e.target.value)} />
                  )}
                  <Button variant="contained" size="large" disabled={!reply || saving} onClick={send}>
                    {saving ? 'Sending…' : 'Send my reply'}
                  </Button>
                </Stack>
              )}

              <Typography variant="body1" mt={3}>With love,<br /><b>{inv.hosts || inv.couple}</b></Typography>
              {inv.contactPhone && (
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Link href={`tel:${inv.contactPhone}`}>{inv.contactPhone}</Link>
                </Stack>
              )}
            </Box>
          </Paper>
        )}
        {inv && <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>Sent with {inv.brandName}</Typography>}
        {inv && (
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5}>
            {optedOut ? 'You will not receive further messages about this wedding.' : (
              <Link component="button" variant="caption" onClick={async () => {
                const r = await fetch(`${API_ORIGIN}/public/invite/${token}/opt-out`, { method: 'POST' });
                if (r.ok) setOptedOut(true);
              }}>Don't send me further messages about this wedding</Link>
            )}
          </Typography>
        )}
        <LegalFooter />
      </Container>
    </Box>
  );
}
